import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  AdminPanelSettings,
  Analytics,
  Close,
  Dashboard as DashboardIcon,
  EmojiEvents,
  ExitToApp,
  Groups,
  Home,
  Person,
  PersonSearch,
  Psychology,
  Settings,
  SmartToy,
  SportsSoccer,
  SportsScore,
  CardMembership,
  LocalOffer,
  CalendarMonth,
  ToggleOn,
  SystemUpdateAlt,
  DesktopWindows,
  Gavel,
  HelpOutline,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useFeatureFlags from "../hooks/useFeatureFlags";
import colors from "../design-system/colors";
import shadows from "../design-system/shadows";
import { useLicenseTier } from "../hooks/useLicenseTier";
import { canManagePlanning, normalizeRole } from "../utils/planning";
import { useAuth } from '../context/AuthContext';

const getUserInitials = (nombre) => {
  if (!nombre) return "U";
  return nombre
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const Menu = ({ open, toggleDrawer, handleLogout, usuario }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const { isFeatureEnabled } = useFeatureFlags();

  const userId = usuario?.oid || null;
  const userRole = usuario?.rol || 'usuario';
  const tier = useLicenseTier(userId, userRole);
  const isAdmin = normalizeRole(usuario?.rol) === "admin";
  const [canManageTeams, setCanManageTeams] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isAdmin) {
      setCanManageTeams(false);
      return undefined;
    }

    let active = true;
    import("../services/teamManagementService").then(({ getMyTeamManagementSubscriptions }) =>
      getMyTeamManagementSubscriptions()
      .then((subscriptions) => {
        if (!active) return;
        setCanManageTeams(subscriptions.length > 0);
      })
      .catch(() => {
        if (active) setCanManageTeams(false);
      }));
    return () => {
      active = false;
    };
  }, [isAdmin, isAuthenticated]);

  const close = () => typeof toggleDrawer === "function" && toggleDrawer();

  const go = (path) => {
    navigate(path);
    close();
  };

  const navItems = isAuthenticated
    ? [
        { label: t('menuItems.home'), icon: <Home />, path: "/", exact: true },
        { label: t('menuItems.dashboard'), icon: <DashboardIcon />, path: "/dashboard" },
        { divider: true },
        { label: t('menuItems.classification'), icon: <EmojiEvents />, path: "/clasificacion" },
        { label: t('menuItems.matches'), icon: <SportsSoccer />, path: "/partidos", also: ["/actaPartido", "/informePartido"] },
        { label: t('menuItems.teams'), icon: <Groups />, path: "/estadisticasEquipos", also: ["/clubes", "/rankingEquipos"] },
        { label: t('menuItems.players'), icon: <Person />, path: "/estadisticas", also: ["/jugador", "/rankingJugadores"] },
        { label: t('menuItems.referees'), icon: <Gavel />, path: "/estadisticasArbitros" },
        { divider: true },
        { label: t('menuItems.individualAnalysis'), icon: <Analytics />, path: "/analisisIndividual", prefix: true },
        { label: t('menuItems.scouting'), icon: <PersonSearch />, path: "/scouting" },
        { label: t('tactical:title'), icon: <SportsSoccer />, path: "/pizarras" },
        ...(isFeatureEnabled('planificacion') && canManagePlanning(usuario)
          ? [{ label: t('menuItems.planning'), icon: <CalendarMonth />, path: "/planificacion" }]
          : []),
        { label: t('menuItems.liveTag'), icon: <SportsScore />, path: "/live-tag" },
        { divider: true },
        { label: t('menuItems.windowsApp'), icon: <DesktopWindows />, path: "/cliente-windows" },
        { label: t('tutorials:title'), icon: <HelpOutline />, path: "/tutoriales" },
        { divider: true },
        ...(isFeatureEnabled('licencias') ? [{ label: t('menuItems.licenses'), icon: <CardMembership />, path: "/licencias", also: ["/mis-licencias"] }] : []),
        ...(canManageTeams
          ? [{ label: 'Administrar equipos', icon: <Groups />, path: "/administrar-equipos" }]
          : []),
        ...(isFeatureEnabled('chatIA') ? [{ label: t('menuItems.aiAgent'), icon: <SmartToy />, path: "/agente" }] : []),
        ...(isAdmin
          ? [
              { divider: true },
              { label: t('menuItems.adminConsole'), icon: <AdminPanelSettings />, path: "/admin/analytics", prefix: true },
              { label: 'Usuarios y roles', icon: <Groups />, path: "/admin/usuarios" },
              { label: t('menuItems.adminLicenses'), icon: <CardMembership />, path: "/admin/licencias" },
              { label: t('menuItems.adminPromoCodes'), icon: <LocalOffer />, path: "/admin/promo-codes" },
              { label: "Versiones", icon: <SystemUpdateAlt />, path: "/admin/versiones" },
              { label: t('menuItems.featureFlags'), icon: <ToggleOn />, path: "/admin/feature-flags" },
            ]
          : []),
      ]
    : [
        { label: t('menuItems.home'), icon: <Home />, path: "/", exact: true },
        { label: t('menuItems.login'), icon: <Psychology />, action: () => navigate('/login') },
      ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    if (item.prefix) return location.pathname.startsWith(item.path);
    const paths = [item.path, ...(item.also || [])];
    return paths.some((p) => location.pathname === p);
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={close}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: 300,
          backgroundColor: colors.primary[900],
          backgroundImage: `linear-gradient(180deg, ${colors.primary[900]} 0%, ${colors.primary[800]} 100%)`,
          color: colors.neutral.white,
          borderRight: `1px solid ${colors.primary[700]}`,
          boxShadow: shadows.xl,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {/* Brand */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${colors.primary[700]}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/favicon.svg?v=20261027`}
              alt=""
              aria-hidden="true"
              sx={{
                width: 36,
                height: 36,
                display: "block",
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {t('header.appName')}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.primary[300], fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase" }}
              >
                {t('header.appSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={close}
            size="small"
            aria-label={t('header.closeMenu')}
            sx={{ color: colors.primary[300], "&:hover": { color: colors.neutral.white } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Nav items — scrollable so every item (including admin-only entries) stays reachable */}
        <List
          sx={{
            px: 1,
            py: 1,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {navItems.map((item, i) => {
            if (item.divider) {
              return <Divider key={`d-${i}`} sx={{ my: 0.5, borderColor: colors.primary[700] }} />;
            }
            const active = isActive(item);
            return (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => (item.action ? item.action() : go(item.path))}
                  aria-label={item.label}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    borderLeft: `3px solid ${active ? colors.secondary[500] : "transparent"}`,
                    backgroundColor: active ? `${colors.primary[700]}cc` : "transparent",
                    color: active ? colors.neutral.white : colors.primary[100],
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: colors.primary[700],
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 34,
                      color: active ? colors.secondary[500] : colors.primary[300],
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* User footer */}
        {isAuthenticated && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${colors.primary[700]}`,
              backgroundColor: `${colors.primary[800]}cc`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: colors.secondary[500],
                color: colors.primary[900],
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {getUserInitials(usuario?.nombre)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                {usuario?.nombre || t('common:roles.user')}
              </Typography>
              {tier ? (
                <Chip
                  label={tier.label}
                  size="small"
                  sx={{
                    mt: 0.5,
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: tier.bg,
                    color: tier.color,
                  }}
                />
              ) : (
                <Typography variant="caption" sx={{ color: colors.primary[300], fontSize: 11 }} noWrap>
                  {usuario?.rol ? t(`common:roles.${usuario.rol}`, { defaultValue: usuario.rol }) : t('common:roles.user')}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              aria-label={t('header.settings')}
              sx={{ color: colors.primary[300], "&:hover": { color: colors.neutral.white } }}
            >
              <Settings sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t('header.logout')}
              onClick={() => {
                handleLogout();
                close();
              }}
              sx={{ color: colors.primary[300], "&:hover": { color: colors.error.main } }}
            >
              <ExitToApp sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Menu;
